import React, { forwardRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { Button } from 'src/components/ui/button';
import { useResendVerificationEmailMutation } from 'src/services/usersSsoService';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNotification } from 'src/hooks/useNotification';
import { useCountDown } from 'src/hooks/useCountDown';

const ResendVerificationButton = forwardRef(
  (
    {
      disabled = false,
      visible = true,
      onClick = () => {},
      ...props
    }: { disabled?: boolean; visible?: boolean; onClick?: (e?: any) => void; [key: string]: any },
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    const { isRunning, formatted, start } = useCountDown(3600, 'email_verify_countdown', 'm:ss');

    return (
      <Button
        ref={ref}
        disabled={isRunning || disabled}
        variant="red"
        size="xl"
        onClick={(e) => {
          start();
          onClick(e);
        }}
        className={clsx(!visible && 'pointer-events-none opacity-50')}
        {...props}
      >
        <p className="font-bold text-sm">
          {isRunning ? (
            <>
              Resend <span className={''}>in {formatted} min</span>
            </>
          ) : (
            'Resend Verification Email'
          )}
        </p>
      </Button>
    );
  },
);

const VerifyYourEmailText = () => {
  const { isRunning } = useCountDown(3600, 'email_verify_countdown', 'm:ss');

  return (
    <p className={'text-sm font-medium text-[#C11A45B2]'}>
      {isRunning
        ? `You recently resent the verification email. Can’t find the email? Check your spam folder.`
        : 'A verification link has been sent to your inbox. Open it to complete the activation of your AI agent.'}
    </p>
  );
};

export const VerifyYourEmailBanner = () => {
  const loader = useLoader();
  const notification = useNotification();
  const [
    resendVerificationEmail,
    { isLoading: isLoadingResendVerificationEmail, isSuccess: isSuccessResendVerificationEmail },
  ] = useResendVerificationEmailMutation();

  useEffect(() => {
    if (isLoadingResendVerificationEmail) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingResendVerificationEmail]);

  useEffect(() => {
    if (isSuccessResendVerificationEmail) {
      notification.success('Verification email has been sent');
    }
  }, [isSuccessResendVerificationEmail]);

  const onButtonClick = () => {
    resendVerificationEmail(undefined);
  };

  return (
    <div
      className={
        'w-full flex flex-row items-center justify-between py-5 px-7 border border-[#C11A4526] bg-[#FCF2F5] rounded-sm-5'
      }
    >
      <div className={'flex flex-col gap-1'}>
        <p className={'text-xl font-bold text-[#C11A45]'}>
          Verify your email to activate the AI agent
        </p>
        <VerifyYourEmailText />
      </div>
      <div className={'flex flex-col'}>
        <ResendVerificationButton onClick={onButtonClick} />
      </div>
    </div>
  );
};
